import { EventInteractions } from '@eventbrite/ads';
import { ReportAdModal } from '@eventbrite/ads-event-card-extra';
import { HorizontalDotsChunky } from '@eventbrite/eds-iconography';
import { MoreActions } from '@eventbrite/eds-more-actions';
import { withTooltip } from '@eventbrite/eds-tooltip';
import {
    EventCardLink,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTracking,
    getEventCardLinkProps,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { MegaphoneIcon, Typography } from '@eventbrite/marmalade';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedEvent } from '../../../../event-renderer/src/types/events';
import styles from './AdRectangleEventCard.module.scss';
import { analyzeImageColors } from './colorUtils';

enum PromotedActions {
    hideAd = EventInteractions.HideAd,
    reportAd = EventInteractions.ReportAd,
}

interface AdRectangleEventCardProps {
    event: FormattedEvent;
    locale: string;
    matchImageBackgroundColor?: boolean;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onShare?: () => void;
    onSave?: (isSaved: boolean, eventId: string) => void;
    onRemoveEvent?: (eventId: string) => void;
    getTrackingContext?: (interaction: any) => any;
    isAuthenticated?: boolean;
    savedByYou?: boolean;
    affCode?: string;
    statsigLocationString?: string;
    statsigMetadata?: any;
    'data-testid'?: string;
}

/**
 * A standalone promoted event card with horizontal layout and custom purple styling.
 *
 * @param props - The props for AdRectangleEventCard.
 * @param props.event - The FormattedEvent object to display.
 * @param props.locale - The locale to pass to the component.
 * @param props.onClick - Optional click handler.
 * @param props.onShare - Optional share handler.
 * @param props.data-testid - Optional test ID.
 * @returns A React element rendering the ad rectangle event card.
 */
const AdRectangleEventCard: React.FC<AdRectangleEventCardProps> = ({
    event,
    locale,
    matchImageBackgroundColor = false,
    onClick,
    onShare,
    onSave,
    onRemoveEvent,
    getTrackingContext,
    isAuthenticated = false,
    savedByYou = false,
    affCode = 'ebdssbdestsearch',
    statsigLocationString,
    statsigMetadata,
    'data-testid': dataTestId,
}) => {
    // Create tooltip-wrapped MegaphoneIcon
    const MegaphoneIconWithTooltip = withTooltip(MegaphoneIcon);
    const [backgroundColor, setBackgroundColor] =
        useState<string>('transparent');
    const [textColor, setTextColor] = useState<string>('#000000');
    const [showReportModal, setReportModalVisibility] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLSpanElement>(null);

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    // Temporary test location - remove this when done testing
    const testLocationString = 'Test Venue • San Francisco, CA';

    const linkProps = getEventCardLinkProps({
        event,
        affCode,
        onClick,
    });

    const eventCardTrackingProps = {
        statsigLocationString,
        event,
        statsigMetadata,
    };

    const actionHandler = (action: PromotedActions) => {
        switch (action) {
            case PromotedActions.hideAd:
                onRemoveEvent?.(event.id);
                sendActionMetrics(event, PromotedActions.hideAd, containerRef);
                break;
            case PromotedActions.reportAd:
                setReportModalVisibility(true);
                break;
            default:
                break;
        }
    };

    const sendActionMetrics = (
        event: FormattedEvent,
        action: PromotedActions,
        element: React.RefObject<HTMLSpanElement>,
    ) => {
        const interactionEvent = new CustomEvent('event-interaction', {
            detail: {
                id: event.id,
                action,
                event,
            },
            bubbles: true,
        });

        element.current?.dispatchEvent(interactionEvent);
    };

    // the following methods are used to try to extract a background color from the event image
    const handleImageAnalysis = async (image: HTMLImageElement) => {
        try {
            const result = await analyzeImageColors(image);
            setBackgroundColor(result.backgroundColor);
            setTextColor(result.textColor);
        } catch (error) {
            setBackgroundColor('transparent');
            setTextColor('#000000');
        }
    };

    // useEffect handles images already loaded (cached by browser)
    useEffect(() => {
        if (!matchImageBackgroundColor) {
            setBackgroundColor('transparent');
            setTextColor('#000000');
            return;
        }
        if (imageRef.current && imageRef.current.complete) {
            handleImageAnalysis(imageRef.current);
        }
    }, [event.imageUrl, matchImageBackgroundColor]);

    // onLoad handles images loading for the first time
    const handleImageLoad = () => {
        if (!matchImageBackgroundColor) {
            return;
        }
        const currentImage = imageRef.current;
        if (currentImage) {
            setTimeout(() => {
                handleImageAnalysis(currentImage);
            }, 100);
        }
    };

    return (
        <div
            className={styles.adRectangleEventCardWrapper}
            data-testid={dataTestId}
        >
            <div className={styles.adRectangleEventCard}>
                <EventCardTracking {...eventCardTrackingProps}>
                    {/* Image Section */}
                    <EventCardLink {...linkProps}>
                        <div className={styles.imageContainer}>
                            <img
                                ref={imageRef}
                                src={event.imageUrl}
                                alt={event.name}
                                className={styles.eventImage}
                                onLoad={handleImageLoad}
                                crossOrigin="anonymous"
                            />
                        </div>
                    </EventCardLink>

                    {/* Content Section */}
                    <div
                        className={styles.contentContainer}
                        style={{
                            backgroundColor,
                            color: textColor,
                        }}
                    >
                        {/* Text Content Group */}
                        <div className={styles.textContent}>
                            {/* Title Row */}
                            <EventCardLink {...linkProps}>
                                <Typography
                                    variant="body-lg"
                                    className={styles.eventTitle}
                                    as="h3"
                                    style={{ color: textColor }}
                                >
                                    {event.name}
                                </Typography>
                            </EventCardLink>

                            {/* Date Row */}
                            <div className={styles.dateRow}>
                                <Typography
                                    variant="body-sm"
                                    className={styles.eventDate}
                                    style={{ color: textColor }}
                                >
                                    {formattedDateString}
                                </Typography>
                            </div>

                            {/* Location Row */}
                            <div className={styles.locationRow}>
                                {(formattedLocationString ||
                                    testLocationString) && (
                                    <Typography
                                        variant="body-sm"
                                        className={styles.eventLocation}
                                        style={{ color: textColor }}
                                    >
                                        {formattedLocationString ||
                                            testLocationString}
                                    </Typography>
                                )}
                            </div>

                            {/* Promoted Row */}
                            <div className={styles.promotedRow}>
                                <div
                                    className={styles.promotedLabel}
                                    style={
                                        {
                                            '--icon-color': textColor,
                                        } as React.CSSProperties
                                    }
                                >
                                    <Typography
                                        variant="body-sm"
                                        className={styles.promotedText}
                                        style={{ color: textColor }}
                                    >
                                        Promoted
                                    </Typography>
                                    <div className={styles.megaphoneWrapper}>
                                        <MegaphoneIconWithTooltip
                                            color={textColor}
                                            size="xsmall"
                                            className={styles.promotedIcon}
                                            aria-label="Promoted event listing"
                                            tooltipId="tooltip-promoted-info"
                                            tooltipStyle="phab"
                                            enhancedA11y={true}
                                            tooltipText={
                                                <Typography variant="body-sm">
                                                    {gettext(
                                                        'Event organizers pay for these premium spots so they can reach people searching for similar things to do.',
                                                    )}
                                                </Typography>
                                            }
                                            tooltipArrowLocation="left"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions Row */}
                        <div
                            className={styles.actionsRow}
                            style={
                                {
                                    '--icon-color': textColor,
                                    '--background-color': backgroundColor,
                                } as React.CSSProperties
                            }
                        >
                            <span ref={containerRef}>
                                <MoreActions
                                    items={[
                                        {
                                            content: gettext('Hide this ad'),
                                            value: PromotedActions.hideAd,
                                        },
                                        {
                                            content: gettext('Report this ad'),
                                            value: PromotedActions.reportAd,
                                        },
                                    ]}
                                    style="dark"
                                    iconType={<HorizontalDotsChunky />}
                                    title={gettext('Promoted event actions')}
                                    dropdownAlign="left"
                                    onAction={actionHandler}
                                />
                            </span>
                            <EventCardSaveAction
                                isAuthenticated={!!isAuthenticated}
                                savedByYou={savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                                onClick={(isSaved, eventId) => {
                                    onSave?.(isSaved, eventId);
                                }}
                                actionStyle="outline"
                            />
                            <EventCardShareAction
                                shareOptions={{
                                    eventId: event.id,
                                    eventName: event.name,
                                    eventUrl: event.url,
                                    isMobile: false,
                                    twitterHandle: '',
                                    serverUrl: '',
                                    facebookOptions: {
                                        appId: '28218816837',
                                        version: 'v2.8',
                                        xfbml: true,
                                        page: '',
                                        locale: 'en_US',
                                    },
                                    utmOptions: {
                                        'utm-campaign': 'social',
                                        'utm-content': 'attendeeshare',
                                        'utm-medium': 'discovery',
                                        'utm-term': '',
                                    },
                                }}
                                onClick={onShare}
                                actionStyle="outline"
                            />
                        </div>
                    </div>
                </EventCardTracking>
            </div>
            {showReportModal && getTrackingContext && (
                <ReportAdModal
                    event={event}
                    handleClose={() => setReportModalVisibility(false)}
                    onSuccessClose={() => onRemoveEvent?.(event.id)}
                    getTrackingContext={getTrackingContext}
                />
            )}
        </div>
    );
};

export default AdRectangleEventCard;
